import { defineStore, getActivePinia, acceptHMRUpdate } from 'pinia';
import { ROADMAP_REQUEST_FIELDS, SPECIAL_ACTION_IDS } from '@/roadmaps/constants/roadmaps.constants';
import * as api from '@/vuex/modules/roadmaps/api/roadmaps.api';
import { asyncQueue } from '@/vuex/modules/roadmaps/async-queue/async-queue';
import filter from 'lodash/filter';
import keyBy from 'lodash/keyBy';
import mapValues from 'lodash/mapValues';
import pick from 'lodash/pick';

interface Column {
    id: string;
    index: number;
    [key: string]: unknown;
}

interface ColumnsState {
    columns: Record<string, Column>;
}

export function getInitialState (): ColumnsState {
    return {
        columns: {},
    };
}

export const useRoadmapsColumnsStore = defineStore('roadmaps/columns', {
    state: getInitialState,
    getters: {
        columns (): Record<string, Column> {
            const rawState = getActivePinia()!.state.value['roadmaps/columns'] as ColumnsState;

            return {
                ...rawState.columns,
                ...(asyncQueue.queuedUpdates.columns as Record<string, Column>),
            };
        },
        orderedColumns (): Column[] {
            return Object.values(this.columns).sort((a, b) => a.index - b.index);
        },
        columnById () {
            return (columnId: string): Column | undefined => this.columns[columnId];
        },
        columnIds (): string[] {
            return Object.keys(this.columns);
        },
        columnCount (): number {
            return Object.keys(this.columns).length;
        },
    },
    actions: {
        setMap ({ columns }: { columns: Record<string, unknown> }) {
            this.$patch((state) => {
                state.columns = mapValues(columns, (column) => Object.freeze(column)) as Record<string, Column>;
            });
        },
        updateMapAtId ({ id, column }: { id: string; column: Partial<Column> }) {
            this.$patch((state) => {
                state.columns[id] = Object.freeze(Object.assign({}, state.columns[id], column)) as Column;
            });
        },
        async updateColumn (columnToUpdate: Column): Promise<Column> {
            const task = async () => {
                const { useRoadmapsStore } = await import('@/stores/roadmaps.store');
                const { currentRoadmap } = useRoadmapsStore();
                const payload = {
                    column: pick(columnToUpdate, ROADMAP_REQUEST_FIELDS.column),
                    roadmapVersionTimestamp: currentRoadmap?.lastUpdatedAt,
                };
                const column = await api.updateColumn(currentRoadmap!.id, payload);
                this.updateMapAtId({ id: column.id, column });

                return column;
            };

            return asyncQueue.enqueue({
                task,
                updates: keyBy([columnToUpdate], 'id'),
                updateType: 'columns',
            });
        },
        async createColumn ({ columnToCreate, index }: { columnToCreate: Partial<Column>; index: number }): Promise<Record<string, Column>> {
            const task = async () => {
                const { useRoadmapsStore } = await import('@/stores/roadmaps.store');
                const { currentRoadmap } = useRoadmapsStore();
                const payload = {
                    column: pick(columnToCreate, ROADMAP_REQUEST_FIELDS.column),
                    index,
                    roadmapVersionTimestamp: currentRoadmap?.lastUpdatedAt,
                };
                const columns = await api.createColumn(currentRoadmap!.id, payload);
                this.setMap({ columns });

                return columns;
            };

            return asyncQueue.enqueue({
                task,
                updates: keyBy(
                    [
                        {
                            id: SPECIAL_ACTION_IDS.CREATE_COLUMN,
                            index,
                            ...columnToCreate,
                        },
                        ...Object.values(this.columns)
                            .slice(index)
                            .map((column) => ({
                                ...column,
                                index: column.index + 1,
                            })),
                    ],
                    'id',
                ),
                updateType: 'columns',
            });
        },
        async deleteColumn (columnToDeleteId: string): Promise<unknown> {
            const task = async () => {
                const { useRoadmapsStore } = await import('@/stores/roadmaps.store');
                const roadmapsStore = useRoadmapsStore();
                const { currentRoadmap } = roadmapsStore;
                const payload = {
                    roadmapVersionTimestamp: currentRoadmap?.lastUpdatedAt,
                };
                const updatedRoadmap = await api.deleteColumn(currentRoadmap!.id, columnToDeleteId, payload);
                await roadmapsStore.setOne(updatedRoadmap);

                return updatedRoadmap;
            };

            const columnsWithoutDeleted = filter(this.columns, (column) => column.id !== columnToDeleteId);

            return asyncQueue.enqueue({
                task,
                updates: keyBy(columnsWithoutDeleted, 'id'),
                updateType: 'columns',
            });
        },
    },
});

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useRoadmapsColumnsStore, module.hot));
}
